/**
 * Room Template Selector Component
 * 
 * Grid component for selecting room templates in design mode
 * Date: 15-10-2025 16:00:00
 * 
 * @module components/molecules/RoomTemplateSelector
 */

import React, { useState, useMemo } from 'react';
import { 
  Box, 
  Grid, 
  Card, 
  Text, 
  Badge, 
  Group, 
  Stack, 
  Select,
  TextInput,
  ActionIcon,
  Tooltip,
  Loader,
  Center,
  Alert
} from '@mantine/core';
import { 
  IconSearch, 
  IconFilter, 
  IconX,
  IconRuler,
  IconUsers
} from '@tabler/icons-react';
import { useRoomTemplates } from '../../hooks/api/useRoomTemplates';
import type { 
  RoomTemplate, 
  RoomTemplateCategory, 
  RoomTemplateFilters 
} from '@shared/types/room-template';
import { 
  ROOM_TEMPLATE_CATEGORY_LABELS
} from '@shared/types/room-template';

// =============================================================================
// INTERFACES
// =============================================================================

interface RoomTemplateSelectorProps {
  selectedTemplate?: RoomTemplate | null;
  onTemplateSelect: (template: RoomTemplate) => void;
  categoryFilter?: RoomTemplateCategory;
  onCategoryChange?: (category: RoomTemplateCategory | null) => void;
  showFilters?: boolean;
  compact?: boolean;
}

// =============================================================================
// COMPONENT
// =============================================================================

/**
 * Room Template Selector Component
 * 
 * Displays a grid of room templates with filtering and selection capabilities
 * Used in design mode for selecting templates to create rooms
 * 
 * @param props - Component props
 * @returns React component
 */
export const RoomTemplateSelector: React.FC<RoomTemplateSelectorProps> = ({
  selectedTemplate,
  onTemplateSelect,
  categoryFilter,
  onCategoryChange,
  showFilters = true,
  compact = false
}) => {
  // Local state for filters
  const [searchTerm, setSearchTerm] = useState('');
  const [localCategoryFilter, setLocalCategoryFilter] = useState<RoomTemplateCategory | null>(categoryFilter || null);

  // Build filters object
  const filters: RoomTemplateFilters = useMemo(() => {
    const result: RoomTemplateFilters = {
      isActive: true
    };
    
    if (localCategoryFilter) {
      result.category = localCategoryFilter;
    }
    
    if (searchTerm) {
      result.search = searchTerm;
    }
    
    return result;
  }, [localCategoryFilter, searchTerm]);

  // Fetch templates
  const { data, isLoading, error } = useRoomTemplates(filters);

  // Handle category filter change
  const handleCategoryChange = (value: string | null) => {
    const category = value as RoomTemplateCategory | null;
    setLocalCategoryFilter(category);
    onCategoryChange?.(category);
  };

  // Clear filters
  const clearFilters = () => {
    setSearchTerm('');
    setLocalCategoryFilter(null);
    onCategoryChange?.(null);
  };

  // Category options for select
  const categoryOptions = Object.entries(ROOM_TEMPLATE_CATEGORY_LABELS).map(([value, label]) => ({
    value,
    label
  }));

  // Loading state
  if (isLoading) {
    return (
      <Center p="xl">
        <Stack align="center" gap="md">
          <Loader size="md" />
          <Text size="sm" c="dimmed">Loading templates...</Text>
        </Stack>
      </Center>
    );
  }

  // Error state
  if (error) {
    return (
      <Alert color="red" title="Error Loading Templates">
        {error.message || 'Failed to load room templates'}
      </Alert>
    );
  }

  // No templates found
  if (!data?.templates || data.templates.length === 0) {
    return (
      <Center p="xl">
        <Stack align="center" gap="md">
          <Text size="lg" c="dimmed">No templates found</Text>
          <Text size="sm" c="dimmed">
            {searchTerm || localCategoryFilter 
              ? 'Try adjusting your filters' 
              : 'No room templates available'
            }
          </Text>
          {(searchTerm || localCategoryFilter) && (
            <ActionIcon 
              variant="subtle" 
              onClick={clearFilters}
              title="Clear filters"
            >
              <IconX size={16} />
            </ActionIcon>
          )}
        </Stack>
      </Center>
    );
  }

  return (
    <Box>
      {/* Filters */}
      {showFilters && (
        <Stack gap="md" mb="md">
          <Group gap="sm">
            <TextInput
              placeholder="Search templates..."
              leftSection={<IconSearch size={16} />}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ flex: 1 }}
              size="sm"
            />
            <Select
              placeholder="All Categories"
              leftSection={<IconFilter size={16} />}
              data={categoryOptions}
              value={localCategoryFilter || null}
              onChange={handleCategoryChange}
              clearable
              size="sm"
              style={{ minWidth: 150 }}
            />
            {(searchTerm || localCategoryFilter) && (
              <Tooltip label="Clear filters">
                <ActionIcon 
                  variant="subtle" 
                  onClick={clearFilters}
                  size="sm"
                >
                  <IconX size={16} />
                </ActionIcon>
              </Tooltip>
            )}
          </Group>
        </Stack>
      )}

      {/* Templates Grid */}
      <Grid gutter="sm">
        {data.templates.map((template) => (
          <Grid.Col key={template.id} span={compact ? 12 : 6}>
            <Card
              padding={compact ? "sm" : "md"}
              radius="md"
              withBorder
              style={{
                cursor: 'pointer',
                borderColor: selectedTemplate?.id === template.id ? '#228be6' : undefined,
                backgroundColor: selectedTemplate?.id === template.id ? '#f0f8ff' : template.color,
                transition: 'all 0.2s ease',
                opacity: selectedTemplate?.id === template.id ? 1 : 0.8
              }}
              onClick={() => onTemplateSelect(template)}
              onMouseEnter={(e) => {
                if (selectedTemplate?.id !== template.id) {
                  e.currentTarget.style.opacity = '1';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }
              }}
              onMouseLeave={(e) => {
                if (selectedTemplate?.id !== template.id) {
                  e.currentTarget.style.opacity = '0.8';
                  e.currentTarget.style.transform = 'translateY(0)';
                }
              }}
            >
              <Stack gap="xs">
                {/* Template Name and Category */}
                <Group justify="space-between" align="flex-start">
                  <Text 
                    fw={500} 
                    size={compact ? "sm" : "md"}
                    style={{ 
                      color: selectedTemplate?.id === template.id ? '#1c7ed6' : undefined 
                    }}
                  >
                    {template.name}
                  </Text>
                  <Badge 
                    size="xs" 
                    variant="light"
                    color={selectedTemplate?.id === template.id ? 'blue' : 'gray'}
                  >
                    {ROOM_TEMPLATE_CATEGORY_LABELS[template.category]}
                  </Badge>
                </Group>

                {/* Description */}
                {template.description && !compact && (
                  <Text size="xs" c="dimmed" lineClamp={2}>
                    {template.description}
                  </Text>
                )}

                {/* Template Properties */}
                <Group gap="sm" justify="space-between">
                  <Group gap="xs">
                    <Tooltip label="Size">
                      <Group gap={4}>
                        <IconRuler size={12} />
                        <Text size="xs">
                          {template.defaultWidth} × {template.defaultHeight}
                        </Text>
                      </Group>
                    </Tooltip>
                  </Group>
                  
                  <Group gap="xs">
                    <Tooltip label="Capacity">
                      <Group gap={4}>
                        <IconUsers size={12} />
                        <Text size="xs">{template.defaultCapacity}</Text>
                      </Group>
                    </Tooltip>
                  </Group>
                </Group>

                {/* Specialties */}
                {template.allowedSpecialties.length > 0 && !compact && (
                  <Group gap="xs">
                    <Text size="xs" c="dimmed">Specialties:</Text>
                    <Group gap={4}>
                      {template.allowedSpecialties.slice(0, 2).map((specialty, index) => (
                        <Badge key={index} size="xs" variant="outline">
                          {specialty}
                        </Badge>
                      ))}
                      {template.allowedSpecialties.length > 2 && (
                        <Text size="xs" c="dimmed">
                          +{template.allowedSpecialties.length - 2} more
                        </Text>
                      )}
                    </Group>
                  </Group>
                )}
              </Stack>
            </Card>
          </Grid.Col>
        ))}
      </Grid>
    </Box>
  );
};
